import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, CheckCircle2, Heart } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Review() {
  usePageMeta(
    "Leave a review — Hope Chest",
    "Tell us what you think of Hope Chest. Praise, ideas, or what's missing — we read every word."
  );
  const [params] = useSearchParams();
  const token = params.get("token");
  const initialType = params.get("type") === "feedback" ? "feedback" : "love";
  const [type, setType] = useState(initialType);
  const [firstName, setFirstName] = useState("");
  const [message, setMessage] = useState("");
  const [allowPublish, setAllowPublish] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  useEffect(() => {
    if (!token) { setInvalidToken(true); return; }
    axios.get(`${API}/public/review/lookup?token=${encodeURIComponent(token)}`)
      .then((r) => setFirstName(r.data.first_name))
      .catch(() => setInvalidToken(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please write a few words"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/review`, {
        token,
        type,
        message,
        allow_publish: type === "love" && allowPublish,
        name_for_publish: displayName.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit — try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (invalidToken) {
    return (
      <div className="min-h-screen bg-[#FCFAF8] flex items-center justify-center p-6">
        <div className="hc-card p-8 max-w-md text-center">
          <h1 className="font-display text-2xl mb-2">This link has expired</h1>
          <p className="text-[#7A736E] mb-5">If you'd still like to share your thoughts, please email us directly.</p>
          <a href="mailto:support@organizedhope.online" className="text-[#3A5A4A] underline">support@organizedhope.online</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935]">
      <nav className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
      </nav>

      <main className="max-w-2xl mx-auto px-6 pt-6 pb-20">
        {done ? (
          <div className="hc-card p-10 text-center bg-gradient-to-br from-[#D8E2D8]/30 to-white" data-testid="review-success">
            <CheckCircle2 className="w-14 h-14 text-[#3A5A4A] mx-auto mb-4" />
            <h1 className="font-display text-3xl mb-3">Thank you, {firstName || "friend"}.</h1>
            <p className="text-[#7A736E] leading-relaxed max-w-md mx-auto">
              {type === "love"
                ? "Your words just made my day. I read every single review — and if you allowed it, yours may end up on our homepage so other caregivers know they're not alone."
                : "Thank you for telling me what's missing. I read every word and your feedback shapes what we build next. I may reach out directly to ask follow-up questions."}
            </p>
            <p className="text-sm text-[#7A736E] mt-6">— Michael, founder of Organized Hope</p>
            <Link to="/" className="inline-block mt-6 text-sm text-[#3A5A4A] underline">Back to Hope Chest</Link>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">A note from Michael</p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold mb-3" data-testid="review-heading">
              Hi {firstName || "there"}, thank you for being here.
            </h1>
            <p className="text-[#7A736E] mb-6 leading-relaxed">
              I built Hope Chest because watching families struggle to keep track of a loved one's care broke my heart.
              Two weeks in, your honest reaction matters more than you know.
            </p>

            {/* Type switcher */}
            <div className="flex gap-2 mb-6 flex-wrap">
              <button
                onClick={() => setType("love")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${type === "love" ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#7A736E] hover:bg-[#E8DCC8]"}`}
                data-testid="review-type-love"
              >
                <Star className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" fill={type === "love" ? "currentColor" : "none"} />
                I love it
              </button>
              <button
                onClick={() => setType("feedback")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${type === "feedback" ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#7A736E] hover:bg-[#E8DCC8]"}`}
                data-testid="review-type-feedback"
              >
                Tell me what's missing
              </button>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="review-form">
              <div>
                <Label htmlFor="rev-msg">
                  {type === "love" ? "What do you love about Hope Chest?" : "What would make Hope Chest better for you?"}
                </Label>
                <Textarea
                  id="rev-msg"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={type === "love"
                    ? "Even one sentence helps. Tell me about a moment when Hope Chest made your day easier."
                    : "Bug, missing feature, confusing flow — anything goes. The more specific the better."}
                  className="rounded-xl mt-1.5"
                  data-testid="review-message"
                  required
                />
              </div>

              {type === "love" && (
                <div className="hc-card p-4 bg-[#F3EFEA]/50 border-[#E8DCC8]">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowPublish}
                      onChange={(e) => setAllowPublish(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#3A5A4A]"
                      data-testid="review-allow-publish"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">You can share my words to help other caregivers</p>
                      <p className="text-xs text-[#7A736E] mt-1">
                        We may publish your kind words on our homepage so other families know Hope Chest is real and human. We'll only show your first name (or whatever you put below).
                      </p>
                    </div>
                  </label>
                  {allowPublish && (
                    <div className="mt-3 pl-7">
                      <Label htmlFor="rev-name" className="text-xs text-[#7A736E]">Display name (optional)</Label>
                      <Input
                        id="rev-name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={`${firstName || "First name"} (default)`}
                        className="rounded-xl mt-1.5 max-w-xs"
                        data-testid="review-display-name"
                      />
                    </div>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-8 py-6"
                data-testid="review-submit"
              >
                {submitting ? "Sending…" : (type === "love" ? "Send my review" : "Send feedback")}
              </Button>
              <p className="text-xs text-[#7A736E]">
                This goes straight to Michael. <Link to="/privacy" className="underline">Privacy policy</Link>.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
